/**
 * CTA Card — smooth morphing SVG wave animation.
 * Uses Catmull-Rom → cubic bezier conversion for genuinely smooth curves.
 */
class CtaCardWaves extends HTMLElement {
  connectedCallback() {
    this._animId = null;
    const paths = this.querySelectorAll(".cta-card__wave path");
    if (!paths.length) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      paths.forEach((p, i) => p.setAttribute("d", this._buildPath(0, i)));
      return;
    }

    this._paths = Array.from(paths);
    const speed = parseFloat(this.dataset.waveSpeed) || 5;
    const mul = speed / 5;

    this._configs = [
      { speed: 0.012 * mul, amplitude: 35, frequency: 0.003, phase: 0 },
      { speed: 0.016 * mul, amplitude: 25, frequency: 0.005, phase: 2.1 },
      { speed: 0.021 * mul, amplitude: 18, frequency: 0.007, phase: 4.2 },
    ];

    if (this._paths[3]) {
      this._configs.push({ speed: 0.026 * mul, amplitude: 12, frequency: 0.009, phase: 6.3 });
    }

    const animate = () => {
      this._configs.forEach((cfg, i) => {
        if (!this._paths[i]) return;
        cfg.phase += cfg.speed;
        this._paths[i].setAttribute("d", this._buildPath(cfg.phase, i, cfg.amplitude, cfg.frequency));
      });
      this._animId = requestAnimationFrame(animate);
    };

    this._animId = requestAnimationFrame(animate);
  }

  /**
   * Sample points along a composite sine wave, then convert to a smooth
   * cubic bezier spline via Catmull-Rom interpolation.
   */
  _buildPath(phase, layerIndex, amplitude = 25, frequency = 0.005) {
    const W = 1440;
    const H = 320;
    const yBase = H * 0.4;
    const samples = 24; // enough points for smooth curves

    // 1. Generate sample points along the wave
    const pts = [];
    for (let i = 0; i <= samples; i++) {
      const x = (i / samples) * W;
      const t = x * frequency + phase + layerIndex * 1.5;
      const y =
        yBase +
        Math.sin(t) * amplitude +
        Math.sin(t * 0.6 + phase * 0.4) * (amplitude * 0.4) +
        Math.sin(t * 1.8 + layerIndex) * (amplitude * 0.15);
      pts.push({ x, y });
    }

    // 2. Build SVG path: move to bottom-left, line to first point,
    //    smooth cubic beziers through all points, close at bottom-right
    let d = `M0,${H} L${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;

    // Catmull-Rom to cubic bezier conversion
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;

      // Catmull-Rom tangents → cubic bezier control points
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
    }

    d += ` L${W},${H} Z`;
    return d;
  }

  disconnectedCallback() {
    if (this._animId) cancelAnimationFrame(this._animId);
  }
}

if (!customElements.get("cta-card-waves")) {
  customElements.define("cta-card-waves", CtaCardWaves);
}
